import { useState, useCallback, useEffect, useRef } from 'react';
import { useGlobalFeatureFlag } from '@/lib/featureFlags';

interface VirtualizationConfig {
  itemCount: number;
  itemHeight: number;
  containerHeight: number;
  overscan?: number;
}

interface VirtualizationResult {
  visibleRange: { start: number; end: number };
  totalHeight: number;
  offsetY: number;
  containerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Week 2 Optimization: Timeline Virtualization
 * 
 * Performance target: 70% loading time reduction for large timelines
 * Feature flag: ENABLE_TIMELINE_VIRTUALIZATION (default: false)
 * 
 * Implementation:
 * - Virtual scrolling (only render visible items)
 * - Lazy render items outside viewport
 * - Progressive rendering with requestIdleCallback
 */
export function useTimelineVirtualization(config: VirtualizationConfig): VirtualizationResult {
  // ✅ Hardcoded since 2025-10-10 - Always use timeline virtualization
  const isEnabled = true;
  const { itemCount, itemHeight, containerHeight, overscan = 3 } = config;
  
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const handleScroll = useCallback(() => {
    if (!isEnabled || !containerRef.current) return;
    setScrollTop(containerRef.current.scrollTop);
  }, [isEnabled]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isEnabled) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isEnabled]);

  // Calculate visible range
  const visibleStart = Math.floor(scrollTop / itemHeight);
  const visibleEnd = Math.ceil((scrollTop + containerHeight) / itemHeight);
  
  const start = Math.max(0, visibleStart - overscan);
  const end = Math.min(itemCount, visibleEnd + overscan);

  const totalHeight = itemCount * itemHeight;
  const offsetY = start * itemHeight;

  // If disabled, render all items
  if (!isEnabled) {
    return {
      visibleRange: { start: 0, end: itemCount },
      totalHeight: itemCount * itemHeight,
      offsetY: 0,
      containerRef
    };
  }

  return {
    visibleRange: { start, end },
    totalHeight,
    offsetY,
    containerRef
  };
}
